import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

// V5.1 WP6 — RC51-VCE-005: ZELI voice reliability telemetry (Zelligio Admin only). Thresholds V1–V8 are the §6
// PROVISIONAL engineering targets (Founder C11 direction) — device acceptance stays PENDING until the Founder verifies it.
const STATUS_TONE = {
  within_provisional: "bg-green-50 text-green-800 border-green-200",
  exceeds_provisional: "bg-red-50 text-red-800 border-red-200",
  insufficient_data: "bg-sand text-warmgrey border-border",
  device_gate_pending: "bg-amber-50 text-amber-800 border-amber-200",
};
const STATUS_LABEL = { within_provisional: "Within provisional target", exceeds_provisional: "Exceeds provisional target", insufficient_data: "No data yet", device_gate_pending: "Founder device gate" };

function ThresholdCard({ t }) {
  const val = t.p95_ms != null ? `p95 ${Math.round(t.p95_ms)} ms · max ${Math.round(t.max_ms)} ms` : t.value_pct != null ? `${t.value_pct} %` : t.value != null ? `${t.value}` : "—";
  return (
    <div className={`rounded-md border p-3 ${STATUS_TONE[t.status] || ""}`} data-testid={`voice-threshold-${t.id}`} data-status={t.status}>
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide">{t.id}</p>
        <p className="text-[10px] uppercase tracking-wide">{STATUS_LABEL[t.status] || t.status}</p>
      </div>
      <p className="mt-1 text-sm">{t.label}</p>
      <p className="mt-1 text-xs">Measured: <span className="font-medium" data-testid={`voice-threshold-${t.id}-value`}>{val}</span> · n={t.n}</p>
      <p className="text-xs">Provisional target: {t.target}</p>
    </div>
  );
}

function Counts({ c }) {
  const rows = [["Captures", c.captures], ["Transcripts", c.transcripts], ["Submitted by voice", c.submitted], ["Answers", c.answers],
    ["Empty captures", c.empty_captures], ["Cancelled", c.cancelled], ["Interrupted", c.interrupted], ["Retries", c.retries],
    ["STT ok (server)", c.server_transcribed], ["STT failed (server)", c.server_transcribe_failed], ["Playbacks", c.playbacks],
    ["Playback interrupted", c.playback_interrupted], ["Mic denied", c.mic_denied], ["Unsupported browser", c.unsupported], ["Errors", c.errors]];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5" data-testid="voice-counts">
      {rows.map(([l, v]) => (
        <div key={l} className="rounded-md border border-border bg-white p-2">
          <p className="text-[10px] uppercase tracking-wide text-warmgrey">{l}</p>
          <p className="text-lg font-medium text-charcoal" data-testid={`voice-count-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</p>
        </div>
      ))}
    </div>
  );
}

export default function AdminVoiceTelemetry() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [includeTest, setIncludeTest] = useState(true);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/copilot/admin/voice/telemetry", { params: { days, include_test: includeTest } });
      setData(r.data); setErr(null);
    } catch (e) { setErr(e.response?.data?.detail?.message || "Could not load voice telemetry."); }
  }, [days, includeTest]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-voice-telemetry-page">
      <div className="mb-6">
        <h1 className="font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">ZELI Voice Telemetry</h1>
        <p className="mt-1 text-sm text-warmgrey">Per-session capture, transcription, playback and interruption reliability. Thresholds are <strong>provisional engineering targets</strong> — not Founder-approved acceptance.</p>
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="voice-telemetry-filters">
        {[1, 7, 30].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`voice-window-${d}`}
            className={`rounded-full border px-3 py-1 text-xs ${days === d ? "border-terracotta bg-terracotta text-white" : "border-border text-charcoal"}`}>{d === 1 ? "24 h" : `${d} days`}</button>
        ))}
        <label className="ml-2 flex items-center gap-1 text-xs text-charcoal"><input type="checkbox" checked={includeTest} onChange={(e) => setIncludeTest(e.target.checked)} data-testid="voice-include-test" /> include QA tenants</label>
        <button onClick={load} className="rounded-full border border-border px-3 py-1 text-xs" data-testid="voice-refresh">Refresh</button>
      </div>
      {err && <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="voice-telemetry-error">{err}</p>}
      {!data && !err && <p className="text-sm text-warmgrey">Loading…</p>}
      {data && (
        <div className="space-y-6">
          <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="voice-device-gate">{data.device_acceptance}</p>
          <Counts c={data.counts} />
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-4" data-testid="voice-thresholds">
            {Object.values(data.thresholds).map((t) => <ThresholdCard key={t.id} t={t} />)}
          </div>
          <div>
            <h2 className="mb-2 text-base font-medium text-charcoal md:text-lg">Sessions ({data.sessions.length})</h2>
            {data.sessions.length === 0 && <p className="rounded-md border border-border bg-white p-4 text-sm text-warmgrey" data-testid="voice-sessions-empty">No voice activity in this window.</p>}
            <div className="space-y-2">
              {data.sessions.map((s) => (
                <div key={s.session_id} className="rounded-md border border-border bg-white p-3 text-xs" data-testid="voice-session-row" data-session-id={s.session_id}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono text-charcoal">{s.session_id}</span>
                    <span className="text-warmgrey">{s.dealer_role || "—"} · {s.is_test_data ? "QA tenant" : "dealer"} · {String(s.last_event_at || "").replace("T", " ").slice(0, 19)}</span>
                  </div>
                  <p className="mt-1 text-charcoal">captures {s.counts.captures} · transcripts {s.counts.transcripts} · empty {s.counts.empty_captures} · STT failed {s.counts.server_transcribe_failed} · retries {s.counts.retries} · cancelled {s.counts.cancelled} · interrupted {s.counts.interrupted} · playbacks {s.counts.playbacks}</p>
                  {Object.keys(s.counts.failures_by_reason).length > 0 && <p className="mt-1 text-red-800">failures: {Object.entries(s.counts.failures_by_reason).map(([k, v]) => `${k} ×${v}`).join(", ")}</p>}
                  {s.exceeds.length > 0 && <p className="mt-1 text-red-800">exceeds provisional: {s.exceeds.join(", ")}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
